import { withDevtools } from '@ngrx-toolkit/core';
import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import type { OrgTreeNodeDto } from '../../../shared';
import { OrgChartApiService } from '../../services/org-chart-api.service';
import { OrgUnitSettingsNavigation } from './org-unit-settings-navigation';
import {
  collectExpandableIds,
  findCanDelete,
  findChildParent,
  findOeParent,
  findPositionAssignees,
  findPositionRoleIds,
  resolveEditInitial,
  type OrgChartExpansion,
} from './org-chart.model';
import type {
  OrgChartCreateRequest,
  OrgChartDeleteTarget,
  OrgChartEditRequest,
  OrgChartMoveRequest,
  OrgChartSettingsRequest,
  OrgChartViewPermissionsRequest,
  OrgOePanelRequest,
  OrgPanelRequest,
  OrgUnitSettingsTarget,
} from './org-chart.types';

interface OrgChartState {
  tree: OrgTreeNodeDto | null;
  organizationId: string;
  dropListIds: string[];
  /** OEs whose card shows its list of Stellen. */
  expandedOeIds: string[];
  /** Stellen whose row shows its persons. */
  expandedPositionIds: string[];
  /** OEs whose card shows its list of stations. */
  expandedStationListOeIds: string[];
  panelRequest: OrgPanelRequest | null;
  pendingDelete: OrgChartDeleteTarget | null;
  viewPermissionsRoleIds: string[] | null;
  /** The position whose roles the shield's panel shows and may change. */
  viewPermissionsPositionId: string | null;
  settingsUnit: OrgUnitSettingsTarget | null;
  /** The person whose details the sheet shows, as on the persons page. */
  previewPersonId: string | null;
  moving: boolean;
  deleting: boolean;
  moveError: string | null;
  deleteError: string | null;
  reloadRevision: number;
}

const initialState: OrgChartState = {
  tree: null,
  organizationId: '',
  dropListIds: [],
  expandedOeIds: [],
  expandedPositionIds: [],
  expandedStationListOeIds: [],
  panelRequest: null,
  pendingDelete: null,
  viewPermissionsRoleIds: null,
  viewPermissionsPositionId: null,
  settingsUnit: null,
  previewPersonId: null,
  moving: false,
  deleting: false,
  moveError: null,
  deleteError: null,
  reloadRevision: 0,
};

/**
 * Page-scoped source of truth for org-chart interactions.
 *
 * Node templates are created dynamically by ng-diagram, so they communicate
 * through this store instead of relaying Subjects through OrgChartComponent
 * outputs. Provide one instance on the page that hosts the chart.
 */
export const OrgChartStore = signalStore(
  withDevtools('org chart store'),
  withState(initialState),
  withComputed((store) => ({
    expandedOeIdSet: computed<ReadonlySet<string>>(
      () => new Set(store.expandedOeIds()),
    ),
    expandedPositionIdSet: computed<ReadonlySet<string>>(
      () => new Set(store.expandedPositionIds()),
    ),
    expandedStationListOeIdSet: computed<ReadonlySet<string>>(
      () => new Set(store.expandedStationListOeIds()),
    ),
    /** What the toolbar's expand buttons open, recomputed per tree. */
    expandable: computed(() => collectExpandableIds(store.tree())),
    error: computed(() => store.moveError() ?? store.deleteError()),
    /**
     * The edit request for the unit currently being configured.
     *
     * Derived rather than stored: editing the unit is now the first entry
     * *inside* its configuration panel, so the two are open at once — and a
     * second mutable field could disagree with `settingsUnit` about which unit
     * that is. Null while no panel is open, or while the tree does not yet
     * hold the unit.
     */
    unitEditRequest: computed<OrgOePanelRequest | null>(() => {
      const unit = store.settingsUnit();
      if (!unit) return null;

      const request = { kind: 'orgUnit', id: unit.id } as const;
      const initial = resolveEditInitial(store.tree(), request);
      const canDelete = findCanDelete(store.tree(), request);
      const parent = findOeParent(store.tree(), unit.id);
      if (!initial || canDelete === null || !parent) return null;

      return {
        mode: 'edit',
        kind: 'orgUnit',
        id: unit.id,
        initial,
        canDelete,
        parent,
      };
    }),
    sheetOpen: computed(
      () =>
        store.panelRequest() !== null ||
        store.viewPermissionsRoleIds() !== null ||
        store.settingsUnit() !== null ||
        store.previewPersonId() !== null,
    ),
  })),
  withComputed((store) => ({
    expansion: computed<OrgChartExpansion>(() => ({
      oeIds: store.expandedOeIdSet(),
      positionIds: store.expandedPositionIdSet(),
      stationListOeIds: store.expandedStationListOeIdSet(),
    })),
    /** Whether every OE with Stellen shows them. False on a tree with none. */
    allPositionListsExpanded: computed(() => {
      const { oeIds } = store.expandable();
      const open = store.expandedOeIdSet();
      return oeIds.length > 0 && oeIds.every((id) => open.has(id));
    }),
    /**
     * Whether every list is open: Stellen, persons and stations. False on a
     * tree with nothing to open.
     */
    everythingExpanded: computed(() => {
      const { oeIds, positionIds, stationListOeIds } = store.expandable();
      const openOes = store.expandedOeIdSet();
      const openPositions = store.expandedPositionIdSet();
      const openStationLists = store.expandedStationListOeIdSet();
      return (
        oeIds.length + stationListOeIds.length > 0 &&
        oeIds.every((id) => openOes.has(id)) &&
        positionIds.every((id) => openPositions.has(id)) &&
        stationListOeIds.every((id) => openStationLists.has(id))
      );
    }),
  })),
  withMethods((store) => {
    const api = inject(OrgChartApiService);
    const navigation = inject(OrgUnitSettingsNavigation);

    const requestReload = (): void => {
      patchState(store, { reloadRevision: store.reloadRevision() + 1 });
    };

    return {
      setContext(tree: OrgTreeNodeDto | null, organizationId: string): void {
        if (
          tree === store.tree() &&
          organizationId === store.organizationId()
        ) {
          return;
        }

        const organizationChanged = organizationId !== store.organizationId();
        patchState(store, {
          tree,
          organizationId,
          ...(organizationChanged
            ? {
                dropListIds: [],
                expandedOeIds: [],
                expandedPositionIds: [],
                expandedStationListOeIds: [],
                panelRequest: null,
                pendingDelete: null,
                viewPermissionsRoleIds: null,
                viewPermissionsPositionId: null,
                settingsUnit: null,
                previewPersonId: null,
                moveError: null,
                deleteError: null,
              }
            : {}),
        });
      },

      setDropListIds(dropListIds: string[]): void {
        patchState(store, { dropListIds });
      },

      setExpanded(positionId: string, expanded: boolean): void {
        const current = store.expandedPositionIds();
        const alreadyExpanded = current.includes(positionId);
        if (alreadyExpanded === expanded) return;
        patchState(store, {
          expandedPositionIds: expanded
            ? [...current, positionId]
            : current.filter((id) => id !== positionId),
        });
      },

      setOeExpanded(oeId: string, expanded: boolean): void {
        const current = store.expandedOeIds();
        const alreadyExpanded = current.includes(oeId);
        if (alreadyExpanded === expanded) return;
        patchState(store, {
          expandedOeIds: expanded
            ? [...current, oeId]
            : current.filter((id) => id !== oeId),
        });
      },

      setStationListExpanded(oeId: string, expanded: boolean): void {
        const current = store.expandedStationListOeIds();
        const alreadyExpanded = current.includes(oeId);
        if (alreadyExpanded === expanded) return;
        patchState(store, {
          expandedStationListOeIds: expanded
            ? [...current, oeId]
            : current.filter((id) => id !== oeId),
        });
      },

      /** Open every OE's list of Stellen; the other lists stay as they are. */
      expandAllPositionLists(): void {
        patchState(store, { expandedOeIds: store.expandable().oeIds });
      },

      /** Close every OE's list of Stellen; the other lists stay as they are. */
      collapseAllPositionLists(): void {
        patchState(store, { expandedOeIds: [] });
      },

      /** Open every list: Stellen, persons and stations. */
      expandAll(): void {
        const { oeIds, positionIds, stationListOeIds } = store.expandable();
        patchState(store, {
          expandedOeIds: oeIds,
          expandedPositionIds: positionIds,
          expandedStationListOeIds: stationListOeIds,
        });
      },

      collapseAll(): void {
        patchState(store, {
          expandedOeIds: [],
          expandedPositionIds: [],
          expandedStationListOeIds: [],
        });
      },

      async move(request: OrgChartMoveRequest): Promise<void> {
        if (store.moving()) return;
        patchState(store, { moving: true, moveError: null });
        try {
          await api.movePersonPosition(
            request.personPositionId,
            request.toPositionId,
          );
        } catch {
          patchState(store, { moveError: 'organigram.errors.moveFailed' });
        } finally {
          patchState(store, { moving: false });
          requestReload();
        }
      },

      edit(request: OrgChartEditRequest): void {
        const initial = resolveEditInitial(store.tree(), request);
        const canDelete = findCanDelete(store.tree(), request);
        if (!initial || canDelete === null) return;

        let panelRequest: OrgOePanelRequest;
        if (request.kind === 'position' || request.kind === 'station') {
          panelRequest = {
            mode: 'edit',
            kind: request.kind,
            id: request.id,
            initial,
            canDelete,
          };
        } else {
          // Only an OE carries a placement. Missing means the tree no longer
          // holds the node, and there is nothing to edit.
          const parent = findOeParent(store.tree(), request.id);
          if (!parent) return;
          panelRequest = {
            mode: 'edit',
            kind: 'orgUnit',
            id: request.id,
            initial,
            canDelete,
            parent,
          };
        }

        patchState(store, {
          panelRequest,
          viewPermissionsRoleIds: null,
          viewPermissionsPositionId: null,
          settingsUnit: null,
          previewPersonId: null,
        });
      },

      create(request: OrgChartCreateRequest): void {
        let panelRequest: OrgPanelRequest;
        if (request.kind === 'orgUnit') {
          const organizationId = store.organizationId();
          if (!organizationId) return;
          const parent = findChildParent(store.tree(), request.contextId);
          if (!parent) return;
          panelRequest = {
            mode: 'create',
            kind: 'orgUnit',
            parentOeId: request.contextId,
            organizationId,
            parent,
          };
        } else if (request.kind === 'position' || request.kind === 'station') {
          panelRequest = {
            mode: 'create',
            kind: request.kind,
            organizationUnitId: request.contextId,
          };
        } else {
          panelRequest = {
            mode: 'create',
            kind: 'person',
            positionId: request.contextId,
            assignees:
              findPositionAssignees(store.tree(), request.contextId) ?? [],
          };
        }
        patchState(store, {
          panelRequest,
          viewPermissionsRoleIds: null,
          viewPermissionsPositionId: null,
          settingsUnit: null,
          previewPersonId: null,
        });
      },

      remove(target: OrgChartDeleteTarget): void {
        patchState(store, {
          pendingDelete: target,
          deleteError: null,
        });
      },

      viewPermissions(request: OrgChartViewPermissionsRequest): void {
        const roleIds = findPositionRoleIds(store.tree(), request.id);
        if (!roleIds) return;
        patchState(store, {
          panelRequest: null,
          viewPermissionsRoleIds: roleIds,
          viewPermissionsPositionId: request.id,
          settingsUnit: null,
          previewPersonId: null,
        });
      },

      /**
       * Open the per-OE configuration panel. Reuses `resolveEditInitial` to look
       * the OE up in the tree — it already yields exactly the name and type the
       * panel header shows, and no-ops when the id is not in the visible tree.
       */
      openSettings(request: OrgChartSettingsRequest): void {
        const initial = resolveEditInitial(store.tree(), {
          kind: 'orgUnit',
          id: request.id,
        });
        if (!initial) return;
        patchState(store, {
          panelRequest: null,
          viewPermissionsRoleIds: null,
          viewPermissionsPositionId: null,
          settingsUnit: {
            id: request.id,
            name: initial.name,
            type: initial.type,
          },
          previewPersonId: null,
        });
      },

      /**
       * Show a person's details in the sheet — the same preview the persons
       * page opens on a row, so the chart does not send anyone away to read
       * who sits in a position.
       */
      previewPerson(personId: string): void {
        patchState(store, {
          panelRequest: null,
          viewPermissionsRoleIds: null,
          viewPermissionsPositionId: null,
          settingsUnit: null,
          previewPersonId: personId,
        });
      },

      closeSheet(): void {
        // The whole breadcrumb goes with the sheet. It lives in a root-provided
        // store that outlives the panel, so without this the next preview
        // opened on the previous one's trail — and, for the rule editor, at
        // the full screen width it had asked for.
        //
        // Not on switching units: clicking one gear after another keeps the
        // section, which is what the memory is for. Closing ends the work.
        navigation.reset();
        patchState(store, {
          panelRequest: null,
          viewPermissionsRoleIds: null,
          viewPermissionsPositionId: null,
          settingsUnit: null,
          previewPersonId: null,
        });
      },

      cancelDelete(): void {
        if (store.deleting()) return;
        patchState(store, { pendingDelete: null });
      },

      async confirmDelete(): Promise<void> {
        const target = store.pendingDelete();
        if (!target || store.deleting()) return;
        patchState(store, { deleting: true, deleteError: null });
        try {
          if (target.kind === 'orgUnit') {
            await api.deleteUnit(target.id);
          } else if (target.kind === 'position') {
            await api.deletePosition(target.id);
          } else if (target.kind === 'station') {
            await api.deleteStation(target.id);
          } else {
            await api.deletePersonPosition(target.id);
          }
          // Close any open edit sheet — the deleted entity is gone.
          patchState(store, {
            panelRequest: null,
            viewPermissionsRoleIds: null,
            viewPermissionsPositionId: null,
            settingsUnit: null,
            previewPersonId: null,
          });
          requestReload();
        } catch {
          patchState(store, {
            deleteError: 'organigram.errors.deleteFailed',
          });
        } finally {
          patchState(store, {
            deleting: false,
            pendingDelete: null,
          });
        }
      },

      saved(): void {
        patchState(store, { panelRequest: null });
        requestReload();
      },

      /**
       * The roles were changed from the shield's panel. It stays open on the
       * new set, which is now what a further change is measured against; the
       * chart is reloaded so the position carries it too.
       */
      positionRolesSaved(roleIds: string[]): void {
        patchState(store, { viewPermissionsRoleIds: [...roleIds] });
        requestReload();
      },

      /**
       * The previewed person was edited. The preview stays — it already shows
       * the saved values — but the chart may still carry the old name.
       */
      personSaved(): void {
        requestReload();
      },

      /** The previewed person was deleted: nothing left to show, nor to list. */
      personDeleted(): void {
        patchState(store, { previewPersonId: null });
        requestReload();
      },
    };
  }),
);

/** Stable CdkDropList id for a position. */
export function orgChartDropListId(positionId: string): string {
  return `pos-drop:${positionId}`;
}

/** Inverse of {@link orgChartDropListId}. */
export function positionIdFromDropList(dropListId: string): string {
  return dropListId.replace(/^pos-drop:/, '');
}
